import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { CatalogTreeCategory, CatalogTreeLab } from '../../types/content';
import Button from '../UI/Button';
import { useAuth } from '../../context/AuthContext';
import { isOwner } from '../../utils/roles';
import { confirmAction } from '../../utils/confirmAction';
import { openLearnerTab } from '../../utils/openLearnerTab';

const CatalogTreePanel: React.FC<{ kind: 'courses' | 'labs' }> = ({ kind }) => {
  const { user } = useAuth();
  const owner = isOwner(user);
  const [categories, setCategories] = useState<CatalogTreeCategory[]>([]);
  const [unlinked, setUnlinked] = useState<CatalogTreeLab[]>([]);
  const [openCats, setOpenCats] = useState<number[]>([]);
  const [openCourses, setOpenCourses] = useState<number[]>([]);
  const [openModules, setOpenModules] = useState<number[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState('');
  const [unlinkedOpen, setUnlinkedOpen] = useState(true);

  const load = () => {
    contentApi.catalogTree().then((r) => {
      const next = r.data.categories || [];
      setCategories(next);
      setUnlinked(r.data.unlinkedLabs || []);
      setOpenCats((current) => (current.length || !next[0] ? current : [next[0].id]));
    }).catch(() => undefined);
  };

  useEffect(() => { load(); }, []);

  const toggle = (list: number[], id: number, set: (next: number[]) => void) => {
    set(list.includes(id) ? list.filter((item) => item !== id) : [...list, id]);
  };

  const setCourseActive = async (id: number, isActive: boolean) => {
    await contentApi.setCourseActive(id, isActive);
    setMessage(isActive ? 'Course is active.' : 'Course marked inactive.');
    load();
  };

  const setLabActive = async (id: number, isActive: boolean) => {
    await contentApi.setLabActive(id, isActive);
    setMessage(isActive ? 'Lab is active.' : 'Lab marked inactive.');
    load();
  };

  const archiveCourse = async (id: number, title: string) => {
    const ok = await confirmAction({
      title: 'Archive this course?',
      description: `“${title}” will be deactivated (soft delete).`,
      confirmLabel: 'Archive',
      cancelLabel: 'Keep it',
      variant: 'danger',
    });
    if (!ok) return;
    await contentApi.archiveCourse(id);
    setMessage('Course archived.');
    load();
  };

  const permanentDeleteCourse = async (id: number, title: string) => {
    const ok = await confirmAction({
      title: 'Delete course permanently?',
      description: `“${title}” will be permanently removed. This cannot be undone.`,
      confirmLabel: 'Delete permanently',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    await contentApi.permanentDeleteCourse(id);
    setMessage('Course permanently deleted.');
    load();
  };

  const archiveLab = async (id: number, name: string) => {
    const ok = await confirmAction({
      title: 'Archive this lab?',
      description: `“${name}” will be deactivated (soft delete).`,
      confirmLabel: 'Archive',
      cancelLabel: 'Keep it',
      variant: 'danger',
    });
    if (!ok) return;
    await contentApi.archiveLab(id);
    setMessage('Lab archived.');
    load();
  };

  const permanentDeleteLab = async (id: number, name: string) => {
    const ok = await confirmAction({
      title: 'Delete lab permanently?',
      description: `“${name}” will be permanently removed. This cannot be undone.`,
      confirmLabel: 'Delete permanently',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    await contentApi.permanentDeleteLab(id);
    setMessage('Lab permanently deleted.');
    load();
  };

  const owners = [...new Map(
    categories.flatMap((category) => category.courses)
      .filter((course) => course.authorId)
      .map((course) => [course.authorId, course.authorName || `Owner ${course.authorId}`])
  ).entries()];

  const unlinkedBlock = kind === 'labs' && unlinked.length > 0 ? (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-3 text-left font-semibold"
        onClick={() => setUnlinkedOpen((open) => !open)}
      >
        {unlinkedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Unlinked labs ({unlinked.length})
      </button>
      {unlinkedOpen && (
        <div className="px-4 pb-4 space-y-2">
          {unlinked.map((lab) => (
            <div key={lab.id} className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
              <Link to={`/admin/labs/${lab.id}/edit`} className={`font-medium ${!lab.isActive ? 'text-error-700' : ''}`}>{lab.name}</Link>
              {!lab.isActive ? <span className="text-xs font-semibold text-error-600">Inactive</span> : null}
              <div className="flex items-center gap-2 flex-wrap">
                <Button type="button" size="sm" variant="ghost" onClick={() => setLabActive(lab.id, !lab.isActive)}>
                  {lab.isActive ? 'Deactivate' : 'Activate'}
                </Button>
                {lab.slug ? (
                  <button type="button" className="text-sm text-primary-700" onClick={() => openLearnerTab(`/labs/${lab.slug}`)}>View</button>
                ) : null}
                <Link to={`/admin/labs/${lab.id}/edit`} className="text-sm text-primary-700">Edit</Link>
                <button type="button" className="text-sm text-error-600" onClick={() => archiveLab(lab.id, lab.name)}>Archive</button>
                {owner ? (
                  <button type="button" className="text-sm text-error-700 font-medium" onClick={() => permanentDeleteLab(lab.id, lab.name)}>
                    Delete permanently
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div>
      {message && <p className="mb-3 text-sm text-success-700">{message}</p>}
      {unlinkedBlock}
      {kind === 'courses' && owners.length > 0 && (
        <label className="mb-3 block text-sm text-gray-700 max-w-sm">
          Course owner
          <select className="mt-1 w-full rounded-md border-gray-300" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">All course owners</option>
            {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
      )}
      <div className="space-y-2">
        {categories.map((category) => (
          <div key={`${category.id}-${category.name}`} className="rounded-xl border border-gray-200 bg-white">
            <button
              type="button"
              className="w-full flex items-center gap-2 px-4 py-3 text-left font-semibold"
              onClick={() => toggle(openCats, category.id, setOpenCats)}
            >
              {openCats.includes(category.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {category.name}
            </button>
            {openCats.includes(category.id) && (
              <div className="px-4 pb-4 space-y-2">
                {category.courses.filter((course) => !ownerId || String(course.authorId) === ownerId).map((course) => (
                  <div key={course.id} className="rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2 px-3 py-2 flex-wrap">
                      <button type="button" onClick={() => toggle(openCourses, course.id, setOpenCourses)}>
                        {openCourses.includes(course.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <Link to={`/my-courses/${course.id}/edit`} className={`flex-1 font-medium ${!course.isActive ? 'text-error-700' : 'text-gray-900'}`}>
                        {course.title}
                        {!course.isActive ? <span className="ml-2 text-xs font-semibold text-error-600">Inactive</span> : null}
                        <span className="ml-2 text-xs text-gray-500">{course.status}{course.authorName ? ` · ${course.authorName}` : ''}</span>
                      </Link>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setCourseActive(course.id, !course.isActive)}>
                        {course.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      {course.slug ? (
                        <button type="button" className="text-sm text-primary-700" onClick={() => openLearnerTab(`/courses/${course.slug}`)}>View</button>
                      ) : null}
                      <Link to={`/my-courses/${course.id}/edit`} className="text-sm text-primary-700">Edit</Link>
                      <button type="button" className="text-sm text-error-600" onClick={() => archiveCourse(course.id, course.title)}>Archive</button>
                      {owner ? (
                        <button type="button" className="text-sm text-error-700 font-medium" onClick={() => permanentDeleteCourse(course.id, course.title)}>
                          Delete permanently
                        </button>
                      ) : null}
                    </div>
                    {openCourses.includes(course.id) && (
                      <ul className="px-6 pb-3 space-y-2">
                        {course.modules.map((module) => (
                          <li key={module.id}>
                            <button
                              type="button"
                              className="flex items-center gap-2 text-sm font-medium text-gray-800"
                              onClick={() => toggle(openModules, module.id, setOpenModules)}
                            >
                              {openModules.includes(module.id) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              {module.title}
                              {module.assessmentCount ? <span className="text-xs text-gray-500">· {module.assessmentCount} assessment</span> : null}
                            </button>
                            {openModules.includes(module.id) && (
                              <ul className="ml-6 mt-1 space-y-1">
                                {module.topics.map((topic) => (
                                  <li key={topic.id} className="text-sm text-gray-700">
                                    {topic.title}
                                    {kind === 'labs' && topic.labs.map((lab) => (
                                      <div key={lab.id} className="ml-4 flex items-center gap-2 text-sm flex-wrap">
                                        <Link to={`/admin/labs/${lab.id}/edit`} className={!lab.isActive ? 'text-error-700' : 'text-primary-700'}>{lab.name}</Link>
                                        {lab.slug ? (
                                          <button type="button" className="text-sm text-primary-700" onClick={() => openLearnerTab(`/labs/${lab.slug}`)}>View</button>
                                        ) : null}
                                        {!lab.isActive ? <span className="text-xs font-semibold text-error-600">Inactive</span> : null}
                                        <Button type="button" size="sm" variant="ghost" onClick={() => setLabActive(lab.id, !lab.isActive)}>
                                          {lab.isActive ? 'Deactivate' : 'Activate'}
                                        </Button>
                                        <button type="button" className="text-sm text-error-600" onClick={() => archiveLab(lab.id, lab.name)}>Archive</button>
                                        {owner ? (
                                          <button type="button" className="text-sm text-error-700 font-medium" onClick={() => permanentDeleteLab(lab.id, lab.name)}>
                                            Delete permanently
                                          </button>
                                        ) : null}
                                      </div>
                                    ))}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CatalogTreePanel;
